import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import {
  Aviso,
  Botao,
  Campo,
  EstadoCarregando,
  EstadoErro,
  MenuDeAcoes,
  classesDeBotao,
} from '../../componentes/ui';
import {
  CHAVE_DOS_QUADROS,
  carregarCard,
  carregarQuadro,
  carregarQuadros,
  chaveDoQuadro,
  gravarUltimoQuadro,
  lerUltimoQuadro,
  type AutomacaoAplicada,
  type CardResumo,
  type Quadro,
} from '../../servicos/crm';
import { formatarCnpj } from '../../servicos/formato';
import type { ColunaDaPlanilha } from '../../servicos/planilha';
import { Pagina } from '../Carteira';
import { CalendarioDoPipeline } from './CalendarioDoPipeline';
import { ConfiguracoesDoPipeline } from './ConfiguracoesDoPipeline';
import { DetalheDoCard } from './DetalheDoCard';
import { NovoCard } from './NovoCard';
import { NovoQuadro } from './NovoQuadro';
import { QuadroKanban } from './QuadroKanban';

type Visao = 'kanban' | 'calendario';

/**
 * CRM → Pipeline: as empresas em acompanhamento, em Kanban ou em calendário,
 * num quadro de cada vez ("Comercial", "Operacional"…).
 *
 * ## A URL guarda o quadro, a visão e o card aberto
 *
 * `?quadro=<id>`, `?visao=calendario` e `?card=<id>`: o "voltar" do navegador
 * fecha o card em vez de sair do pipeline, e o link de um card pode ser
 * mandado a quem vai acompanhá-lo. Sem `?quadro`, abre o último quadro usado
 * neste navegador — ou o primeiro.
 *
 * ## O card manda no quadro
 *
 * Os links de card que vêm de fora (cadastro da empresa, Atividades,
 * Relatórios, Prospecção) só dizem o card. Se ele é de outro quadro, a tela
 * vai até o quadro dele — senão o card abriria sobre as colunas erradas.
 */
export function Pipeline(): JSX.Element {
  const [parametros, setParametros] = useSearchParams();
  const visao: Visao = parametros.get('visao') === 'calendario' ? 'calendario' : 'kanban';
  const cardAberto = parametros.get('card');
  const quadroPedido = parametros.get('quadro') ?? lerUltimoQuadro();
  const [novoCard, setNovoCard] = useState<{ colunaId?: string } | null>(null);
  const [configurando, setConfigurando] = useState(false);
  const [criandoQuadro, setCriandoQuadro] = useState(false);
  /** A busca de card: razão social, nome fantasia ou CNPJ, no quadro aberto. */
  const [busca, setBusca] = useState('');
  /** O que a automação fez em outro quadro com o último card movido. */
  const [automacao, setAutomacao] = useState<{
    readonly aplicada: AutomacaoAplicada;
    readonly empresa: string;
  } | null>(null);
  const aoAutomatizar = (aplicada: AutomacaoAplicada, empresa: string): void =>
    setAutomacao({ aplicada, empresa });

  const quadros = useQuery({ queryKey: CHAVE_DOS_QUADROS, queryFn: carregarQuadros });
  const lista = quadros.data ?? [];
  // O pedido só vale se ainda existe (um quadro excluído, um link velho).
  const quadroId =
    quadroPedido !== null && lista.some((q) => q.id === quadroPedido)
      ? quadroPedido
      : (lista[0]?.id ?? null);

  const quadro = useQuery({
    queryKey: chaveDoQuadro(quadroId),
    queryFn: () => carregarQuadro(quadroId),
    // Esperar a lista evita abrir o padrão e logo depois o pedido.
    enabled: quadros.isSuccess,
  });

  // A mesma consulta do detalhe do card: abrir por link não pede o card duas vezes.
  const cardDoLink = useQuery({
    queryKey: ['crm', 'card', cardAberto],
    queryFn: () => carregarCard(cardAberto ?? ''),
    enabled: cardAberto !== null,
  });
  const quadroDoCard = cardDoLink.data?.quadroId;

  const mudarParametro = (chave: string, valor: string | null, substituir = false): void => {
    setParametros(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        if (valor === null) proximos.delete(chave);
        else proximos.set(chave, valor);
        return proximos;
      },
      { replace: substituir },
    );
  };
  const abrirCard = (id: string): void => mudarParametro('card', id);

  /** Trocar de quadro fecha o card aberto — ele é do quadro anterior. */
  const escolherQuadro = (id: string): void => {
    gravarUltimoQuadro(id);
    setAutomacao(null);
    setParametros(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        proximos.set('quadro', id);
        proximos.delete('card');
        return proximos;
      },
      { replace: true },
    );
  };

  /** "Abrir no Operacional": o card que a automação criou ou moveu, no quadro dele. */
  const abrirOndeAAutomacaoPos = (aplicada: AutomacaoAplicada): void => {
    gravarUltimoQuadro(aplicada.quadroId);
    setAutomacao(null);
    setParametros((atual) => {
      const proximos = new URLSearchParams(atual);
      proximos.set('quadro', aplicada.quadroId);
      proximos.set('card', aplicada.cardId);
      return proximos;
    });
  };

  useEffect(() => {
    if (quadroDoCard === undefined || quadroDoCard === '' || quadroDoCard === quadroId) return;
    gravarUltimoQuadro(quadroDoCard);
    mudarParametro('quadro', quadroDoCard, true);
    // `mudarParametro` é recriado a cada render; o que importa é o quadro do card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quadroDoCard, quadroId]);

  const quadroAtual = lista.find((q) => q.id === quadroId) ?? null;

  // Enquanto o card do link não leva ao quadro dele, as colunas são as erradas.
  const dados =
    quadro.data !== undefined && (quadroId === null || quadro.data.id === quadroId)
      ? quadro.data
      : undefined;
  const total = dados?.colunas.reduce((soma, c) => soma + c.cards.length, 0) ?? 0;
  const buscando = busca.trim() !== '';
  const visiveis = useMemo(
    () => (dados === undefined || !buscando ? dados : filtrarCards(dados, busca)),
    [dados, busca, buscando],
  );
  const achados = visiveis?.colunas.reduce((soma, c) => soma + c.cards.length, 0) ?? 0;
  const usosDasTags = useMemo(() => {
    const usos = new Map<string, number>();
    for (const card of dados?.colunas.flatMap((c) => c.cards) ?? []) {
      for (const tagId of card.tagIds) usos.set(tagId, (usos.get(tagId) ?? 0) + 1);
    }
    return usos;
  }, [dados]);

  return (
    <Pagina
      titulo="Pipeline"
      descricao="As empresas em acompanhamento, por etapa. Arraste os cards entre as colunas ou veja no calendário, pela data de execução."
      contagem={total}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <Campo
            rotulo="Buscar card"
            rotuloOculto
            type="search"
            placeholder="Buscar empresa ou CNPJ"
            compacto
            className="w-full sm:w-56"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {/* O quadro inteiro, não só o que a busca mostra — como em Empresas. */}
          <BotaoExtrairXlsx
            titulo={dados === undefined ? 'Pipeline' : `Pipeline - ${dados.nome}`}
            tamanho="pequeno"
            disabled={!dados}
            colunas={dados === undefined ? [] : colunasDaPlanilha(dados)}
            linhas={dados === undefined ? [] : linhasDaPlanilha(dados)}
          />
          <div role="group" aria-label="Visão do pipeline" className="flex gap-1">
            {(
              [
                ['kanban', 'Kanban'],
                ['calendario', 'Calendário'],
              ] as const
            ).map(([id, rotulo]) => (
              <Botao
                key={id}
                tamanho="pequeno"
                variante={visao === id ? 'primario' : 'secundario'}
                aria-pressed={visao === id}
                onClick={() => mudarParametro('visao', id === 'kanban' ? null : id, true)}
              >
                {rotulo}
              </Botao>
            ))}
          </div>
          {/* O quadro aberto; abrir mostra os outros e "+ Quadro". */}
          {quadros.isSuccess && (
            <MenuDeAcoes
              acao="Quadro"
              rotulo={quadroAtual?.nome ?? 'Quadro'}
              largura={260}
              className={classesDeBotao('secundario', 'pequeno', 'max-w-[16rem]')}
              gatilho={
                <>
                  <span className="truncate">{quadroAtual?.nome ?? 'Quadro'}</span>
                  <IconeSetaParaBaixo />
                </>
              }
              itens={[
                ...lista.map((q) => ({
                  rotulo: q.nome,
                  descricao: `${q.cards} ${q.cards === 1 ? 'card' : 'cards'}`,
                  marcado: q.id === quadroId,
                  aoEscolher: () => escolherQuadro(q.id),
                })),
                {
                  rotulo: '+ Quadro',
                  separadoAntes: true,
                  aoEscolher: () => setCriandoQuadro(true),
                },
              ]}
            />
          )}
          {/* Criar card fica em cada coluna ("+ Adicionar empresa"); aqui em cima
              só o que vale para o quadro inteiro. */}
          {/* Mesmo tamanho dos botões de visão, ao lado: um quadrado da altura deles. */}
          <Botao
            variante="secundario"
            tamanho="pequeno"
            className="w-9"
            onClick={() => setConfigurando(true)}
            disabled={!dados}
            aria-label="Configurações do pipeline"
            title="Configurações do pipeline"
          >
            <IconeEngrenagem />
          </Botao>
        </div>
      }
    >
      {quadros.isError || quadro.isError ? (
        <EstadoErro
          titulo="Não foi possível carregar o pipeline"
          mensagem={(quadros.error ?? quadro.error)?.message ?? ''}
          aoTentarNovamente={() => {
            void quadros.refetch();
            void quadro.refetch();
          }}
        />
      ) : dados === undefined ? (
        <EstadoCarregando contexto="o pipeline" />
      ) : (
        <>
          {automacao !== null && (
            <Aviso
              tom="positivo"
              titulo="Automação do CRM"
              className="mb-4"
              aoFechar={() => setAutomacao(null)}
              acao={
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  onClick={() => abrirOndeAAutomacaoPos(automacao.aplicada)}
                >
                  Abrir no {automacao.aplicada.quadroNome}
                </Botao>
              }
            >
              {automacao.empresa} também foi para {automacao.aplicada.quadroNome} ›{' '}
              {automacao.aplicada.colunaNome}.
            </Aviso>
          )}

          {buscando && (
            <Aviso
              tom={achados === 0 ? 'atencao' : 'informacao'}
              className="mb-4"
              aoFechar={() => setBusca('')}
            >
              {achados === 0
                ? `Nenhum card com "${busca.trim()}" neste quadro.`
                : `${achados} de ${total} card(s) com "${busca.trim()}".`}{' '}
              {visao === 'kanban' && 'Para arrastar cards, limpe a busca.'}
            </Aviso>
          )}

          {visao === 'kanban' ? (
            <QuadroKanban
              quadro={visiveis ?? dados}
              semArrastar={buscando}
              aoAbrirCard={abrirCard}
              aoAdicionarNaColuna={(colunaId) => setNovoCard({ colunaId })}
              aoAutomatizar={aoAutomatizar}
            />
          ) : (
            <CalendarioDoPipeline quadro={visiveis ?? dados} aoAbrirCard={abrirCard} />
          )}

          {novoCard !== null && (
            <NovoCard
              aberto
              colunas={dados.colunas}
              {...(novoCard.colunaId === undefined ? {} : { colunaInicial: novoCard.colunaId })}
              aoFechar={() => setNovoCard(null)}
              aoCriar={(card) => {
                setNovoCard(null);
                abrirCard(card.id);
              }}
            />
          )}

          <ConfiguracoesDoPipeline
            aberto={configurando}
            quadro={{ id: dados.id, nome: dados.nome, cards: total }}
            unicoQuadro={lista.length === 1}
            tags={dados.tags}
            usos={usosDasTags}
            aoExcluirQuadro={() => {
              setConfigurando(false);
              const outro = lista.find((q) => q.id !== dados.id);
              if (outro !== undefined) escolherQuadro(outro.id);
            }}
            aoFechar={() => setConfigurando(false)}
          />

          {cardAberto !== null && (
            <DetalheDoCard
              key={cardAberto}
              cardId={cardAberto}
              colunas={dados.colunas}
              tags={dados.tags}
              aoFechar={() => mudarParametro('card', null)}
              aoAutomatizar={aoAutomatizar}
            />
          )}
        </>
      )}

      <NovoQuadro
        aberto={criandoQuadro}
        quadros={lista}
        aoFechar={() => setCriandoQuadro(false)}
        aoCriar={(novo) => {
          setCriandoQuadro(false);
          escolherQuadro(novo.id);
        }}
      />
    </Pagina>
  );
}

/** Sem acento e em minúsculas: "Cooperativa" acha "COOPERATIVA" e "cooperatíva". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * O quadro só com os cards que batem com a busca, nas mesmas colunas: pela
 * razão social, pelo nome fantasia ou pelo CNPJ (com ou sem pontuação).
 */
function filtrarCards(quadro: Quadro, busca: string): Quadro {
  const termo = normalizar(busca.trim());
  const digitos = busca.replace(/\D/g, '');
  const bate = (card: CardResumo): boolean =>
    normalizar(card.razaoSocial ?? '').includes(termo) ||
    normalizar(card.nomeFantasia ?? '').includes(termo) ||
    (digitos.length >= 3 && (card.cnpj ?? '').replace(/\D/g, '').includes(digitos));
  return {
    ...quadro,
    colunas: quadro.colunas.map((c) => ({ ...c, cards: c.cards.filter(bate) })),
  };
}

/** Um card por linha, com a etapa em que está — a ordem é a do quadro. */
type LinhaDoPipeline = CardResumo & { readonly etapa: string };

function linhasDaPlanilha(quadro: Quadro): LinhaDoPipeline[] {
  return quadro.colunas.flatMap((c) => c.cards.map((card) => ({ ...card, etapa: c.nome })));
}

function colunasDaPlanilha(quadro: Quadro): ColunaDaPlanilha<LinhaDoPipeline>[] {
  const nomeDaTag = new Map(quadro.tags.map((t) => [t.id, t.nome]));
  return [
    { titulo: 'Empresa', largura: 44, valor: (c) => c.razaoSocial },
    { titulo: 'Nome fantasia', largura: 28, valor: (c) => c.nomeFantasia },
    { titulo: 'CNPJ', largura: 20, valor: (c) => formatarCnpj(c.cnpj) },
    { titulo: 'Etapa', largura: 20, valor: (c) => c.etapa },
    { titulo: 'Data de execução', formato: 'data', largura: 16, valor: (c) => c.dataExecucao },
    {
      titulo: 'Tags',
      largura: 30,
      valor: (c) =>
        c.tagIds
          .map((id) => nomeDaTag.get(id) ?? '')
          .filter(Boolean)
          .join(', '),
    },
    {
      titulo: 'Acompanham',
      largura: 30,
      valor: (c) => c.participantes.map((p) => p.nome).join(', '),
    },
    {
      titulo: 'Atividades em aberto',
      formato: 'numero',
      largura: 12,
      valor: (c) => c.atividadesPendentes,
    },
    { titulo: 'Próximo prazo', formato: 'data', largura: 14, valor: (c) => c.proximoPrazo },
    { titulo: 'Comentários', formato: 'numero', largura: 12, valor: (c) => c.comentarios },
  ];
}

function IconeSetaParaBaixo(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function IconeEngrenagem(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}
